import React, { useEffect, useRef, useState } from "react";

const loadImage = (src) => new Promise((resolve, reject) => {
  const image = new Image();
  image.crossOrigin = "anonymous";
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error("Could not load the image."));
  image.src = src;
});

const findCalmWallArea = (image) => {
  const sample = document.createElement("canvas");
  sample.width = 96;
  sample.height = Math.max(64, Math.round(96 * image.height / image.width));
  const context = sample.getContext("2d", { willReadFrequently: true });
  context.drawImage(image, 0, 0, sample.width, sample.height);
  const pixels = context.getImageData(0, 0, sample.width, sample.height).data;
  let best = { score: Infinity, x: .5, y: .36, light: .75, warmth: 0 };
  const radius = 8;

  for (let cy = radius; cy < sample.height * .68; cy += 3) {
    for (let cx = radius; cx < sample.width - radius; cx += 3) {
      let sum = 0, sumSq = 0, red = 0, blue = 0, count = 0;
      for (let y = cy - radius; y <= cy + radius; y += 2) {
        for (let x = cx - radius; x <= cx + radius; x += 2) {
          const index = (y * sample.width + x) * 4;
          const value = pixels[index] * .299 + pixels[index + 1] * .587 + pixels[index + 2] * .114;
          sum += value; sumSq += value * value; red += pixels[index]; blue += pixels[index + 2]; count += 1;
        }
      }
      const average = sum / count;
      const variance = Math.max(0, sumSq / count - average * average);
      const edgePenalty = Math.abs(cx / sample.width - .5) * 28;
      const darknessPenalty = average < 55 ? 700 : 0;
      const score = variance + edgePenalty + darknessPenalty;
      if (score < best.score) best = { score, x: cx / sample.width, y: cy / sample.height, light: average / 255, warmth: (red - blue) / count / 255 };
    }
  }
  return best;
};

const AiRoomPreview = ({ artworkUrl, productTitle, artworkHasFrame = false, onClose }) => {
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const roomRef = useRef(null);
  const artRef = useRef(null);
  const dragRef = useRef(null);
  const [roomFile, setRoomFile] = useState(null);
  const [placement, setPlacement] = useState({ x: .5, y: .36, scale: .34 });
  const [frame, setFrame] = useState(artworkHasFrame ? "frameless" : "black");
  const [brightness, setBrightness] = useState(100);
  const [warmth, setWarmth] = useState(0);
  const [shadow, setShadow] = useState(35);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("photo");

  useEffect(() => {
    if (artworkHasFrame) setFrame("frameless");
  }, [artworkHasFrame]);

  useEffect(() => {
    document.body.classList.add("ai-preview-open");
    const closeOnEscape = (event) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", closeOnEscape);
    loadImage(artworkUrl).then((image) => { artRef.current = image; }).catch((err) => setError(err.message));
    return () => {
      document.body.classList.remove("ai-preview-open");
      window.removeEventListener("keydown", closeOnEscape);
      streamRef.current?.getTracks().forEach((track) => track.stop());
    };
  }, [artworkUrl, onClose]);

  const draw = () => {
    const canvas = canvasRef.current, room = roomRef.current, art = artRef.current;
    if (!canvas || !room || !art) return;
    const maxWidth = 1200;
    const ratio = Math.min(1, maxWidth / room.width);
    canvas.width = Math.round(room.width * ratio);
    canvas.height = Math.round(room.height * ratio);
    const context = canvas.getContext("2d");
    context.drawImage(room, 0, 0, canvas.width, canvas.height);

    const artWidth = canvas.width * placement.scale;
    const artHeight = artWidth * art.height / art.width;
    const border = frame === "frameless" ? 0 : Math.max(5, artWidth * .035);
    const x = placement.x * canvas.width - artWidth / 2;
    const y = placement.y * canvas.height - artHeight / 2;
    const colors = { black: "#151718", white: "#f5f2e9", "natural-wood": "#a97945" };
    context.save();
    context.shadowColor = `rgba(0,0,0,${shadow / 100})`;
    context.shadowBlur = Math.max(6, artWidth * .055);
    context.shadowOffsetX = artWidth * .018;
    context.shadowOffsetY = artWidth * .026;
    if (border) { context.fillStyle = colors[frame]; context.fillRect(x - border, y - border, artWidth + border * 2, artHeight + border * 2); }
    context.filter = `brightness(${brightness}%) sepia(${Math.abs(warmth)}%) saturate(${100 + Math.abs(warmth) * .35}%)`;
    context.drawImage(art, x, y, artWidth, artHeight);
    context.restore();
    context.save();
    context.globalAlpha = .08;
    context.fillStyle = warmth >= 0 ? `rgb(255,${220 - warmth},150)` : `rgb(150,210,255)`;
    context.fillRect(x, y, artWidth, artHeight);
    context.restore();
  };

  useEffect(draw, [placement, frame, brightness, warmth, shadow]);

  const chooseRoom = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return setError("Please choose an image smaller than 10 MB.");
    const url = URL.createObjectURL(file);
    try {
      const image = await loadImage(url);
      roomRef.current = image;
      setRoomFile(file);
      const area = findCalmWallArea(image);
      setPlacement({ x: area.x, y: area.y, scale: .34 });
      setBrightness(Math.round(78 + area.light * 32));
      setWarmth(Math.max(-30, Math.min(30, Math.round(area.warmth * 100))));
      setError("");
      requestAnimationFrame(draw);
    } catch (err) { setError(err.message); }
    finally { URL.revokeObjectURL(url); }
  };

  const pointerDown = (event) => {
    if (!roomRef.current) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = true;
  };
  const pointerMove = (event) => {
    if (!dragRef.current) return;
    const rect = event.currentTarget.getBoundingClientRect();
    setPlacement((old) => ({ ...old, x: Math.max(.08, Math.min(.92, (event.clientX - rect.left) / rect.width)), y: Math.max(.08, Math.min(.92, (event.clientY - rect.top) / rect.height)) }));
  };
  const download = () => {
    const link = document.createElement("a");
    link.download = `${productTitle || "mayachitro"}-room-preview.jpg`;
    link.href = canvasRef.current.toDataURL("image/jpeg", .9);
    link.click();
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  const startCamera = async () => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      return setError("Camera preview is not supported by this browser.");
    }
    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      setMode("ar");
      setRoomFile(null);
      requestAnimationFrame(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      });
    } catch (err) {
      setError(err.name === "NotAllowedError" ? "Camera permission was denied. Allow camera access and try again." : "Could not open the camera.");
    }
  };

  const captureArView = async () => {
    const video = videoRef.current;
    if (!video?.videoWidth) return setError("The camera is still starting. Please wait a moment.");
    const snapshot = document.createElement("canvas");
    snapshot.width = video.videoWidth;
    snapshot.height = video.videoHeight;
    snapshot.getContext("2d").drawImage(video, 0, 0);
    const image = await loadImage(snapshot.toDataURL("image/jpeg", .92));
    roomRef.current = image;
    stopCamera();
    setMode("photo");
    setRoomFile({ name: "ar-camera-capture.jpg" });
    requestAnimationFrame(draw);
  };

  const frameColor = { black: "#151718", white: "#f5f2e9", "natural-wood": "#a97945", frameless: "transparent" }[frame];
  const artRatio = artRef.current ? artRef.current.height / artRef.current.width : .7;

  return <div className="ai-preview-backdrop" role="presentation" onMouseDown={onClose}>
    <section className="ai-preview-modal" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
      <button className="ai-preview-close" type="button" onClick={onClose} aria-label="Close">×</button>
      <div className="ai-preview-heading"><span>Free Room &amp; AR Studio</span><h2>See it in your space</h2><p>Upload a room or use your rear camera. Everything stays on your device; drag the artwork to position it.</p></div>
      <div className="ai-preview-layout">
        <div className="ai-preview-canvas" onPointerDown={pointerDown} onPointerMove={pointerMove} onPointerUp={() => { dragRef.current = false; }}>
          {mode === "ar" ? <div style={{ position: "relative", width: "100%", minHeight: 420, overflow: "hidden", background: "#071014" }}>
            <video ref={videoRef} playsInline muted style={{ width: "100%", minHeight: 420, display: "block", objectFit: "cover" }} />
            <div style={{ position: "absolute", left: `${placement.x * 100}%`, top: `${placement.y * 100}%`, width: `${placement.scale * 100}%`, aspectRatio: `1/${artRatio}`, transform: "translate(-50%,-50%)", padding: frame === "frameless" ? 0 : "1.8%", background: frameColor, boxShadow: `10px 14px 24px rgba(0,0,0,${shadow / 100})`, pointerEvents: "none" }}>
              <img src={artworkUrl} alt="Artwork AR overlay" style={{ width: "100%", height: "100%", display: "block", objectFit: "fill", filter: `brightness(${brightness}%) sepia(${Math.abs(warmth)}%)` }} />
            </div>
          </div> : roomFile ? <canvas ref={canvasRef} aria-label={`${productTitle} in your room`} style={{ width: "100%", height: "auto", display: "block", touchAction: "none", cursor: "move" }} /> : <div className="ai-preview-dropzone"><label><strong>Upload your room</strong><span>JPG, PNG or WebP · up to 10 MB</span><input type="file" accept="image/jpeg,image/png,image/webp" onChange={chooseRoom} /></label><button type="button" onClick={startCamera} style={{ marginTop: 18, padding: "12px 18px", border: 0, borderRadius: 8, background: "#15343b", color: "white", fontWeight: 800, cursor: "pointer" }}>Open AR camera</button></div>}
        </div>
        <div className="ai-preview-controls">
          {mode === "ar" || roomFile ? <>
            <div className="ai-preview-toolbar">{mode === "ar" ? <><button className="ai-preview-generate" type="button" onClick={captureArView}>Capture view</button><button className="ai-preview-replace" type="button" onClick={() => { stopCamera(); setMode("photo"); }}>Close camera</button></> : <><label className="ai-preview-replace">Change photo<input type="file" accept="image/jpeg,image/png,image/webp" onChange={chooseRoom} /></label><button className="ai-preview-replace" type="button" onClick={startCamera}>Switch to AR</button></>}</div>
            {artworkHasFrame ? <small>The uploaded preview already includes its frame.</small> : <div><label htmlFor="free-frame">Frame</label><select id="free-frame" value={frame} onChange={(e) => setFrame(e.target.value)}><option value="black">Black</option><option value="white">White</option><option value="natural-wood">Natural wood</option><option value="frameless">Frameless</option></select></div>}
            <div><label>Artwork size · {Math.round(placement.scale * 100)}%</label><input type="range" min="18" max="65" value={placement.scale * 100} onChange={(e) => setPlacement((old) => ({ ...old, scale: Number(e.target.value) / 100 }))} /></div>
            <details className="ai-preview-advanced"><summary>Fine-tune lighting</summary><div><label>Brightness</label><input type="range" min="65" max="130" value={brightness} onChange={(e) => setBrightness(Number(e.target.value))} /></div><div><label>Warmth</label><input type="range" min="-30" max="30" value={warmth} onChange={(e) => setWarmth(Number(e.target.value))} /></div><div><label>Shadow</label><input type="range" min="0" max="70" value={shadow} onChange={(e) => setShadow(Number(e.target.value))} /></div></details>
            {error && <p className="ai-preview-error">{error}</p>}
            {mode !== "ar" && <button className="ai-preview-generate" type="button" onClick={download}>Save preview</button>}
            <small>Drag the artwork on the image to reposition it.</small>
          </> : <div className="ai-preview-welcome"><strong>Two ways to preview</strong><ol><li><b>Room photo</b><span>Best for a polished image you can save.</span></li><li><b>Live AR camera</b><span>Best for checking placement instantly.</span></li></ol><p>Your images stay on this device.</p>{error && <p className="ai-preview-error">{error}</p>}</div>}
        </div>
      </div>
    </section>
  </div>;
};

export default AiRoomPreview;
